/**
 * T4 Phase 2D — Reconciliation replay engine.
 *
 * Replays a committed `pay_run_items` row against the current engine
 * to verify (a) the engine version is unchanged, (b) the rate version
 * is unchanged, (c) the engine output is byte-equal to the stored
 * snapshot. Returns a `ReplayResult` describing the comparison.
 *
 * Pure function. The caller is responsible for:
 *   - Loading the `pay_run_items` row (incl. `breakdown_snapshot` JSONB).
 *   - Loading the corresponding `timesheets` + `staff_classification_history`
 *     rows the original snapshot saw.
 *   - Loading the original RateContext (resolved by versionId from
 *     `pay_run_items.snapshot_award_rate_version_id`).
 *   - Computing the effective PH flag with the same PH calendar +
 *     state-aware lookup the original snapshot used.
 *   - Persisting the `ReplayResult` into `replay_result_items` via the
 *     `insert_replay_result_items_controlled` RPC (added by the 2D
 *     server-action layer).
 *
 * Cross-engine-version replay is FORBIDDEN by default. If the current
 * engine's `engineVersion` differs from the stored
 * `snapshot_engine_version`, the function throws unless the caller
 * passes `allowVersionDrift: true`. That escape hatch exists so the
 * CI gate (sub-phase 2G) can replay a baseline against a NEW engine
 * version and the resulting drift IS the gate's signal.
 */

import type { AwardEngine } from '../award-engines/types';
import type { RateContext } from '../award-rate-lookup';
import type { ActiveCertificationInput, CertGateRequirement } from '../cert-currency';
import type { ShiftPayBreakdown, ShiftPayCertGateResult } from '../schads';
import type { ResolvedOrdinaryHoursCapWindow } from './ordinary-hours-cap-windows';
import {
  buildShiftEngineInput,
  type ResolverClassification,
  type ResolverTimesheet,
} from './replay-input-resolver';

/**
 * Output of a single replay: the stored snapshot side-by-side with
 * the freshly-computed result, plus byte-equality verdict.
 */
export interface ReplayResult {
  /** Caller-supplied identifier (typically `pay_run_items.id`). */
  payRunItemId: string;
  /** Engine version stamped on the stored snapshot. */
  snapshotEngineVersion: string;
  /** Engine version that produced the replay. */
  replayEngineVersion: string;
  /** SHA of the engine code at snapshot time. */
  snapshotEngineSha: string;
  /** SHA of the engine code at replay time. */
  replayEngineSha: string;
  /** The original committed engine output (read from DB by caller). */
  originalBreakdown: ShiftPayBreakdown;
  /** Fresh engine output. */
  replayedBreakdown: ShiftPayBreakdown;
  /** True if originalBreakdown === replayedBreakdown via canonical JSON. */
  byteEqual: boolean;
  /** When !byteEqual, a JSON-Patch-like diff summary for the UI. */
  divergence?: ReplayDivergence;
}

/**
 * Structured divergence summary for the UI / classifier. Only set when
 * `byteEqual=false`. Top-level fields populated:
 *   - `totalPayDelta`: replayed.grossTotal - original.grossTotal (signed).
 *   - `bandsChanged`: keys of `bands` whose value differs.
 *   - `allowanceLinesChanged`: true if the allowance-line array differs
 *     in length or content.
 */
export interface ReplayDivergence {
  totalPayDelta: number;
  bandsChanged: string[];
  allowanceLinesChanged: boolean;
  /** Free-form note for the operator. */
  note: string;
}

/** Arguments to `replayPayRunItem`. */
export interface ReplayPayRunItemArgs {
  /** Identifier of the pay_run_item being replayed (for the ReplayResult). */
  payRunItemId: string;
  /** The classification history row the original snapshot saw. */
  classification: ResolverClassification;
  /** The timesheet row the original snapshot saw. */
  timesheet: ResolverTimesheet;
  /** Effective PH flag (manual OR state-aware calendar lookup). */
  effectiveIsPublicHoliday: boolean;
  /** Prior-week ordinary hours BEFORE this shift. */
  priorOrdinaryHoursThisWeek: number;
  priorDailyOvertimeInitialTierHours?: number;
  /** Daily ordinary-hours cap used by the original snapshot, if profile-backed. */
  ordinaryHoursLimitPerShift?: number | null;
  /** Approved worker-pattern daily limit used by the original snapshot. */
  ordinaryHoursEmployeePatternLimitPerShift?: number | null;
  /** Weekly ordinary-hours cap used by the original snapshot, if profile-backed. */
  ordinaryHoursLimitPerWeek?: number | null;
  /** Prior cycle ordinary hours BEFORE this shift, if a profile-backed cycle applied. */
  ordinaryHoursWorkedEarlierThisCycle?: number;
  /** Cycle ordinary-hours cap used by the original snapshot, if profile-backed. */
  ordinaryHoursLimitPerCycle?: number | null;
  /** Cycle length carried by the original snapshot, if profile-backed. */
  ordinaryHoursCycleDays?: number | null;
  /** Whether the original snapshot disabled weekly ordinary accumulation. */
  disableWeeklyOrdinaryHoursLimit?: boolean;
  /** Immutable concurrent ordinary-hours windows captured by the original snapshot. */
  ordinaryHoursCapWindows?: ResolvedOrdinaryHoursCapWindow[];
  /** Tenant timezone used by the original snapshot for local day/time windows. */
  timezone?: string | null;
  /**
   * MZ-004 — MA000018 cl 29.2(a) election recorded on the item's pay context
   * (`pay_context_snapshot.publicHolidayElection`); absent = default cash.
   */
  publicHolidayElection?: 'CASH_150' | 'ANNUAL_LEAVE_HOURS' | null;
  /** The original committed engine output. */
  originalBreakdown: ShiftPayBreakdown;
  /** Engine version stamped on the original `pay_run_items` row. */
  snapshotEngineVersion: string;
  /** Engine SHA stamped on the original `pay_run_items` row. */
  snapshotEngineSha: string;
  /** Award engine selected by the original `award_code`. */
  engine: AwardEngine;
  /** RateContext resolved by `snapshot_award_rate_version_id`. */
  rateContext?: RateContext;
  /**
   * Optional cert-gate snapshot captured by the original pay-run item. When
   * omitted, replay falls back to `originalBreakdown.certGateResults`.
   */
  certGateResults?: ShiftPayCertGateResult[];
}

/** Options for replay invocation. */
export interface ReplayOptions {
  /**
   * Allow the engine version of the replay to differ from the stored
   * snapshot. Default `false`: a divergent engine version throws —
   * any resulting diff would be ambiguous (engine drift OR rate
   * drift). The CI gate (sub-phase 2G) uses `true` so it can detect
   * engine drift against a frozen baseline.
   */
  allowVersionDrift?: boolean;
}

/**
 * Sentinel error thrown when cross-engine-version replay is attempted
 * without `allowVersionDrift: true`.
 */
export class ReplayEngineVersionMismatchError extends Error {
  constructor(
    public readonly payRunItemId: string,
    public readonly snapshotEngineVersion: string,
    public readonly replayEngineVersion: string,
  ) {
    super(
      `Replay of pay_run_item ${payRunItemId} refused: snapshot engine version ` +
        `'${snapshotEngineVersion}' differs from current engine version ` +
        `'${replayEngineVersion}'. The snapshot was produced by a different engine; ` +
        `pass allowVersionDrift:true to bypass (CI-gate only — UI must surface a clear ` +
        `signal that drift is engine-version-induced rather than rate-induced).`,
    );
    this.name = 'ReplayEngineVersionMismatchError';
  }
}

/**
 * Compute a structured divergence summary between two breakdowns.
 * Pure function.
 */
function summariseDivergence(
  original: ShiftPayBreakdown,
  replayed: ShiftPayBreakdown,
): ReplayDivergence {
  // eslint-disable-next-line statura/no-floating-point-money -- explicit *100/100 round-to-cents on already-summed engine-emitted grossTotal values; result is a delta surfaced to the operator as a diff summary, not persisted as a regulated wage value.
  const totalPayDelta = Math.round((replayed.grossTotal - original.grossTotal) * 100) / 100;
  const bandsChanged: string[] = [];
  for (const key of Object.keys(original.bands) as Array<keyof typeof original.bands>) {
    if (original.bands[key] !== replayed.bands[key]) bandsChanged.push(String(key));
  }
  const allowanceLinesChanged =
    JSON.stringify(original.allowanceLines) !== JSON.stringify(replayed.allowanceLines);
  const note =
    [
      totalPayDelta !== 0 && `gross drift $${totalPayDelta.toFixed(2)}`,
      bandsChanged.length > 0 && `bands changed: ${bandsChanged.join(', ')}`,
      allowanceLinesChanged && 'allowance lines changed',
    ]
      .filter(Boolean)
      .join('; ') ||
    'no detectable difference (deep-equal mismatch but field-equal — investigate JSON shape)';
  return { totalPayDelta, bandsChanged, allowanceLinesChanged, note };
}

/**
 * Replay a single `pay_run_item` against the current engine. Returns
 * a `ReplayResult` describing the comparison.
 *
 * @throws ReplayEngineVersionMismatchError if the engine version on
 *         the snapshot differs from the current engine and
 *         `allowVersionDrift` is false (default).
 */
export function replayPayRunItem(
  args: ReplayPayRunItemArgs,
  options: ReplayOptions = {},
): ReplayResult {
  const replayEngineVersion = args.engine.engineVersion;
  const replayEngineSha = args.engine.engineRevisionSha;

  if (!options.allowVersionDrift && args.snapshotEngineVersion !== replayEngineVersion) {
    throw new ReplayEngineVersionMismatchError(
      args.payRunItemId,
      args.snapshotEngineVersion,
      replayEngineVersion,
    );
  }

  const replayInput = buildShiftEngineInput({
    classification: args.classification,
    timesheet: args.timesheet,
    effectiveIsPublicHoliday: args.effectiveIsPublicHoliday,
    priorOrdinaryHoursThisWeek: args.priorOrdinaryHoursThisWeek,
    priorDailyOvertimeInitialTierHours: args.priorDailyOvertimeInitialTierHours,
    ordinaryHoursLimitPerShift: args.ordinaryHoursLimitPerShift ?? null,
    ordinaryHoursEmployeePatternLimitPerShift:
      args.ordinaryHoursEmployeePatternLimitPerShift ?? null,
    ordinaryHoursLimitPerWeek: args.ordinaryHoursLimitPerWeek ?? undefined,
    ordinaryHoursWorkedEarlierThisCycle: args.ordinaryHoursWorkedEarlierThisCycle,
    ordinaryHoursLimitPerCycle: args.ordinaryHoursLimitPerCycle ?? null,
    ordinaryHoursCycleDays: args.ordinaryHoursCycleDays ?? null,
    disableWeeklyOrdinaryHoursLimit: args.disableWeeklyOrdinaryHoursLimit,
    ordinaryHoursCapWindows: args.ordinaryHoursCapWindows,
    timezone: args.timezone ?? null,
    publicHolidayElection: args.publicHolidayElection ?? null,
  });
  const certGateSnapshot = args.certGateResults ?? args.originalBreakdown.certGateResults;
  if (certGateSnapshot && certGateSnapshot.length > 0) {
    Object.assign(replayInput, certGateSnapshotToEngineInput(certGateSnapshot));
  }

  const replayedBreakdown = args.engine.calculateWithOvertime(replayInput, args.rateContext);

  const byteEqual = JSON.stringify(args.originalBreakdown) === JSON.stringify(replayedBreakdown);

  return {
    payRunItemId: args.payRunItemId,
    snapshotEngineVersion: args.snapshotEngineVersion,
    replayEngineVersion,
    snapshotEngineSha: args.snapshotEngineSha,
    replayEngineSha,
    originalBreakdown: args.originalBreakdown,
    replayedBreakdown,
    byteEqual,
    divergence: byteEqual
      ? undefined
      : summariseDivergence(args.originalBreakdown, replayedBreakdown),
  };
}

function certGateSnapshotToEngineInput(results: ShiftPayCertGateResult[]): {
  certGateRegistry: Map<string, CertGateRequirement>;
  activeCertifications: ActiveCertificationInput[];
  certGateAsOfDate: string;
} {
  const certGateRegistry = new Map<string, CertGateRequirement>();
  const activeCertifications: ActiveCertificationInput[] = [];
  let certGateAsOfDate = results[0]?.asOfDate ?? '1900-01-01';

  for (const result of results) {
    certGateAsOfDate = result.asOfDate;
    certGateRegistry.set(`${result.awardCode}:${result.allowanceCode}`, {
      id: null,
      tenantId: null,
      awardCode: result.awardCode,
      allowanceCode: result.allowanceCode,
      certificationType: result.certificationType as CertGateRequirement['certificationType'],
      requirementCode: result.requirementCode,
      requirementLabel: result.requirementLabel,
      required: result.required,
      status: 'ACTIVE',
      effectiveFrom: result.asOfDate,
      effectiveTo: null,
      updatedAt: null,
      createdAt: null,
    });
    if (result.status === 'SATISFIED') {
      activeCertifications.push({
        certificationType: result.certificationType,
        requirementCode: result.requirementCode,
        status: 'CURRENT',
        verificationStatus: 'VERIFIED',
        expiresAt: result.asOfDate,
      });
    }
  }

  return { certGateRegistry, activeCertifications, certGateAsOfDate };
}
